import { FiTrash2, FiX } from "react-icons/fi"
import { useTranslation, Trans } from "react-i18next";
import { ChangeEventHandler, useContext, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  DialogContentText,
  Button,
  IconButton,
  FormControl,
  Select,
  SelectChangeEvent,
  MenuItem,
  InputLabel,
  TextField,
  Tooltip,
} from "@mui/material";

import { SessionContext } from "../../../utility/useSession";
import {
  api,
  KmosAllocation,
  KmosV2AllocationDeleteReasonOption,
  RequestStatus,
} from "../../../logic/api";

interface IKmosDialogRemoveAllocationProps {
  allocation: KmosAllocation;
  isDASAllocation: boolean;
  disabled?: boolean;
  onRemoved: () => void;
}

const kmosV2AllocationDeleteReasonOptionMap = new Map([
  [KmosV2AllocationDeleteReasonOption.OTHER, "kmos.dialogs.removeAllocation.reasonForm.select.options.other"],
  [KmosV2AllocationDeleteReasonOption.SUGGESTED_STORAGE_AREA_FULL, "kmos.dialogs.removeAllocation.reasonForm.select.options.suggestedStorageAreaFull"],
  [KmosV2AllocationDeleteReasonOption.SUGGESTED_STORAGE_AREA_NON_EXISTENT, "kmos.dialogs.removeAllocation.reasonForm.select.options.suggestedStorageAreaNonExistent"],
  [KmosV2AllocationDeleteReasonOption.SUGGESTED_STORAGE_AREA_TOO_FAR_AWAY, "kmos.dialogs.removeAllocation.reasonForm.select.options.suggestedStorageAreaTooFarAway"],
  [KmosV2AllocationDeleteReasonOption.SUGGESTED_STORAGE_AREA_CLOSED, "kmos.dialogs.removeAllocation.reasonForm.select.options.suggestedStorageAreaClosed"],
  [KmosV2AllocationDeleteReasonOption.SUGGESTED_STORAGE_AREA_POOR_SPREAD, "kmos.dialogs.removeAllocation.reasonForm.select.options.suggestedStorageAreaPoorSpread"],
  [KmosV2AllocationDeleteReasonOption.SUGGESTED_STORAGE_AREA_NOT_NEEDED, "kmos.dialogs.removeAllocation.reasonForm.select.options.suggestedStorageAreaNotNeeded"],
])

export default function KmosDialogRemoveAllocation({
  allocation,
  onRemoved,
  isDASAllocation,
  disabled,
}: IKmosDialogRemoveAllocationProps) {
  const [open, setOpen] = useState(false);
  const [removalReason, setRemovalReason] = useState<KmosV2AllocationDeleteReasonOption | "">("");
  const [removalComment, setRemovalComment] = useState("");
  const { t } = useTranslation();
  const session = useContext(SessionContext)
  const deleteAllocationAPI = api.hooks.kmosV2.deleteAllocation();
  const submitting = deleteAllocationAPI.status === RequestStatus.loading

  const handleRemoveLane = () => {
    deleteAllocationAPI.send(
      session.current.context,
      {
        id: {
          timeSlotDate: {
            timeSlot: allocation.timeSlot,
            date: allocation.allocationDate,
          },
          locationID: allocation.location.id,
        },
        reason: (removalReason || removalReason === 0) ? {
          option: removalReason,
          comment: removalComment,
        } : undefined
      },
      () => {
        setOpen(false);
        onRemoved();
      }
    );
  };

  const handleSelectChange = (event: SelectChangeEvent) => {
    setRemovalReason(event.target.value as unknown as KmosV2AllocationDeleteReasonOption);
  };

  const handleRemovalCommentChange: ChangeEventHandler<HTMLTextAreaElement> = (e) => {
    setRemovalComment(e.target.value)
  }

  const handleRemoveLaneIconClick = () => {
    if (isDASAllocation) {
      setOpen(true)
    } else {
      handleRemoveLane()
    }
  }

  const handleCloseDialog = () => {
    setOpen(false);
  }

  const isDisabled = () => {
    if (submitting) {
      return true
    }

    if (!isDASAllocation) {
      return false
    }

    return !removalReason && removalReason !== 0
  }

  return (
    <>
      <Tooltip title={t('kmos.dialogs.removeAllocation.iconBtn')}>
        <IconButton disabled={disabled} size="small" color="error" onClick={handleRemoveLaneIconClick}>
          <FiTrash2 size={16} />
        </IconButton>
      </Tooltip>
      <Dialog
        disablePortal
        disableEnforceFocus
        disableAutoFocus
        disableRestoreFocus
        open={open}
        onClose={handleCloseDialog}
        PaperProps={{
          component: "form",
          sx: {
            minWidth: 480,
            textAlign: 'left'
          }
        }}
      >
        <IconButton sx={{ position: 'absolute', top: 4, right: 4 }} onClick={handleCloseDialog}>
          <FiX size={16} />
        </IconButton>
        <DialogTitle>
          {t("kmos.dialogs.removeAllocation.title")}
          <br />
          <DialogContentText
            component="span"
            variant="subtitle1"
          >
            <Trans
              i18nKey="kmos.dialogs.removeAllocation.subtitle"
              values={{ location: allocation.location.name }}
            />
          </DialogContentText>
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column" }}>
          <DialogContentText variant="subtitle2" mt={1}>
            {t("kmos.dialogs.removeAllocation.reasonForm.title")}
          </DialogContentText>
          <FormControl sx={{ my: 1 }} size="small">
            <InputLabel id="lane-remove-feedback-select-label">
              {t("kmos.dialogs.removeAllocation.reasonForm.select.label")}
            </InputLabel>
            <Select
              MenuProps={{
                disablePortal: true
              }}
              labelId="lane-remove-feedback-select-label"
              id="lane-remove-feedback-select"
              value={removalReason as string}
              label={t("kmos.dialogs.removeAllocation.reasonForm.select.label")}
              onChange={handleSelectChange}
            >
              <MenuItem value="" disabled>
                <em>{t("kmos.dialogs.removeAllocation.reasonForm.select.options.default")}</em>
              </MenuItem>
              {
                Object.values(KmosV2AllocationDeleteReasonOption)
                  .filter((v) => !Number.isNaN(Number(v)))
                  .map((v) => (
                    <MenuItem
                      key={v}
                      value={v}
                    >
                      {t(kmosV2AllocationDeleteReasonOptionMap.get(Number(v)) as string)}
                    </MenuItem>
                  ))
              }
            </Select>
          </FormControl>
          {removalReason === KmosV2AllocationDeleteReasonOption.OTHER ? (
            <TextField
              autoFocus
              size="small"
              name="lane-remove-feedback-comment"
              label={t("kmos.dialogs.removeAllocation.reasonForm.additionalComments.label")}
              type="text"
              fullWidth
              multiline
              minRows={2}
              variant="outlined"
              value={removalComment}
              onChange={handleRemovalCommentChange}
            />
          ) : ''}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "flex-end" }}>
          <Button
            disabled={isDisabled()}
            color="error"
            variant="contained"
            onClick={handleRemoveLane}
          >
            {t("kmos.dialogs.removeAllocation.primaryBtn")}
          </Button>
          <Button color="inherit" onClick={handleCloseDialog}>
            {t("kmos.dialogs.removeAllocation.secondaryBtn")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
